import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { i18n as tableI18n } from '../DataTable.i18n';
import { useStyles } from '../DataTable.styles';
import type { DataTableCellValue, DataTableColDef } from '../DataTable.types';
import { i18n } from './DataTableFilterBar.i18n';

export interface DataTableFilterBarProps {
    columns: DataTableColDef[];
    filters: Record<string, DataTableCellValue[]>;
    onChange: (field: string, values: DataTableCellValue[]) => void;
    onClearAll: () => void;
    showSearch?: boolean;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    searchPlaceholder?: string;
    totalLabel?: string;
}

interface OpenState {
    el: HTMLElement;
    field: string;
}

const isSelected = (
    selected: DataTableCellValue[] | undefined,
    options: { value: DataTableCellValue }[],
    value: DataTableCellValue,
): boolean => {
    // Absent ≡ all selected.
    if (selected === undefined) return options.some((o) => o.value === value);
    return selected.includes(value);
};

const getFilterChipLabelSuffix = (
    selected: DataTableCellValue[] | undefined,
    optionCount: number,
    options: { value: DataTableCellValue; label: string }[],
): string => {
    // No filter applied (absent ≡ all selected) renders as a plain title +
    // chevron, no suffix — keeps inactive chips visually quiet.
    if (selected === undefined || selected.length === optionCount) return '';
    const labels = options.filter((o) => selected.includes(o.value)).map((o) => o.label);
    return `: ${labels.join(', ')}`;
};

const FilterCheckbox = ({
    checked,
    onChange,
    label,
    automationId,
    className,
}: {
    checked: boolean;
    onChange: () => void;
    label: string;
    automationId: string | undefined;
    className?: string;
}) => (
    <FormControlLabel
        control={
            <Checkbox
                checked={checked}
                onChange={onChange}
                size="small"
                automation-id={automationId}
            />
        }
        label={label}
        className={className}
    />
);

export const DataTableFilterBar = forwardRef<HTMLDivElement, DataTableFilterBarProps>(
    function DataTableFilterBar(
        {
            columns,
            filters,
            onChange,
            onClearAll,
            showSearch = false,
            searchValue = '',
            onSearchChange,
            searchPlaceholder,
            totalLabel,
        },
        ref,
    ) {
        const intl = useIntl();
        const { classes } = useStyles();
        const [open, setOpen] = useState<OpenState | null>(null);
        const resolvedSearchPlaceholder =
            searchPlaceholder ?? intl.formatMessage(tableI18n.searchPlaceholder);

        const filterableColumns = useMemo(
            () => columns.filter((c) => c.filterOptions && c.filterOptions.length > 0),
            [columns],
        );

        const anyFilterActive = filterableColumns.some((c) => filters[c.field] !== undefined);

        const handleChipClick = useCallback((el: HTMLElement, field: string) => {
            setOpen({ el, field });
        }, []);

        const handleClose = useCallback(() => setOpen(null), []);

        const activeColumn = open ? filterableColumns.find((c) => c.field === open.field) : null;
        const activeOptions = activeColumn?.filterOptions ?? [];
        const activeSelected = open ? filters[open.field] : undefined;
        const activeAllValues = activeOptions.map((o) => o.value);
        const effectiveSelectedCount =
            activeSelected === undefined ? activeOptions.length : activeSelected.length;
        const allSelected = effectiveSelectedCount === activeOptions.length;

        const handleToggleAll = useCallback(() => {
            if (!open) return;
            // "Select all" is one-way — unchecking would leave zero options, which is disallowed.
            if (allSelected) return;
            onChange(open.field, activeAllValues);
        }, [open, allSelected, activeAllValues, onChange]);

        const handleToggleOption = useCallback(
            (value: DataTableCellValue) => {
                if (!open) return;
                const current =
                    activeSelected === undefined ? activeAllValues.slice() : activeSelected.slice();
                const idx = current.indexOf(value);
                if (idx >= 0) {
                    // Keep at least one option selected.
                    if (current.length <= 1) return;
                    current.splice(idx, 1);
                } else {
                    current.push(value);
                }
                onChange(open.field, current);
            },
            [open, activeSelected, activeAllValues, onChange],
        );

        if (filterableColumns.length === 0 && !showSearch && !totalLabel) return null;

        return (
            <Box ref={ref} className={classes.filterBar} automation-id="filter-bar">
                {showSearch && (
                    <TextField
                        value={searchValue}
                        onChange={(e) => onSearchChange?.(e.target.value)}
                        placeholder={resolvedSearchPlaceholder}
                        size="small"
                        className={classes.searchInput}
                        automation-id="filter-search"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                )}
                <Box className={classes.filterBarChips}>
                    {filterableColumns.map((col) => {
                        const selected = filters[col.field];
                        const optionCount = col.filterOptions?.length ?? 0;
                        const isFiltered =
                            selected !== undefined && selected.length !== optionCount;
                        const labelSuffix = getFilterChipLabelSuffix(
                            selected,
                            optionCount,
                            col.filterOptions ?? [],
                        );
                        const chipLabel = (
                            <Box component="span" className={classes.filterChipLabel}>
                                {`${col.headerName}${labelSuffix}`}
                                <KeyboardArrowDownIcon
                                    fontSize="small"
                                    className={classes.filterChipChevron}
                                />
                            </Box>
                        );
                        return (
                            <Chip
                                key={col.field}
                                label={chipLabel}
                                color={isFiltered ? 'primary' : 'default'}
                                variant={isFiltered ? 'filled' : 'outlined'}
                                onClick={(e) => handleChipClick(e.currentTarget, col.field)}
                                onDelete={
                                    isFiltered
                                        ? () =>
                                              onChange(
                                                  col.field,
                                                  col.filterOptions?.map((o) => o.value) ?? [],
                                              )
                                        : undefined
                                }
                                automation-id={`filter-chip-${col.field}`}
                                className={classes.filterChip}
                            />
                        );
                    })}
                </Box>
                {anyFilterActive && (
                    <Button
                        size="small"
                        onClick={onClearAll}
                        automation-id="filter-clear-all"
                        className={classes.filterClearAll}
                    >
                        {intl.formatMessage(i18n.clearAll)}
                    </Button>
                )}
                {totalLabel && (
                    <Box className={classes.totalLabel} automation-id="filter-total-label">
                        {totalLabel}
                    </Box>
                )}
                <Popover
                    open={!!open}
                    anchorEl={open?.el}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <Box className={classes.filterPopoverContent}>
                        <FilterCheckbox
                            checked={allSelected}
                            onChange={handleToggleAll}
                            label={intl.formatMessage(i18n.selectAll)}
                            automationId={open ? `filter-select-all-${open.field}` : undefined}
                            className={classes.filterSelectAll}
                        />
                        {activeOptions.map((opt) => (
                            <FilterCheckbox
                                key={String(opt.value)}
                                checked={isSelected(activeSelected, activeOptions, opt.value)}
                                onChange={() => handleToggleOption(opt.value)}
                                label={opt.label}
                                automationId={
                                    open
                                        ? `filter-option-${open.field}-${String(opt.value)}`
                                        : undefined
                                }
                            />
                        ))}
                    </Box>
                </Popover>
            </Box>
        );
    },
);
